// ENARM Match - Lógica de Scoring MEJORADA (100% Determinístico)
// Versión 2.0 - Con Hard Stops, OR_Component, PROC_Component y Calibración
// Funciones puras: mismo input = mismo output

import { 
  ADN_ESPECIALIDADES, 
  PENALIZADORES, 
  DESTINOS_10_ANIOS,
  NOMBRES_EJES,
  MAPEO_FACTORES,
  PLANTILLAS,
  SCORING_WEIGHTS,
  CATEGORIAS_PROCEDIMENTALES
} from './ENARMMatchData';

// =============================================================================
// CONSTRUIR PERFIL DE USUARIO (0-3 por eje)
// =============================================================================
export function buildUserProfile(respuestasPares) {
  // Inicializar contadores crudos
  const puntosRaw = {
    agudo: 0,
    longitudinal: 0,
    cognitivo: 0,
    procedimientos: 0,
    quirofano: 0,
    emocional: 0,
    imagen: 0,
    sistema: 0,
    pediatria: 0
  };

  // Sumar puntos de cada respuesta
  respuestasPares.forEach(respuesta => {
    const mapeo = respuesta.seleccion === 'A' ? respuesta.mapeoA : respuesta.mapeoB;
    Object.entries(mapeo).forEach(([eje, valor]) => {
      puntosRaw[eje] += valor;
    });
  });

  // Encontrar el máximo absoluto para normalizar
  const maxAbsoluto = Math.max(...Object.values(puntosRaw).map(Math.abs), 1);

  // Normalizar a escala 0-3
  const perfilUsuario = {};
  Object.entries(puntosRaw).forEach(([eje, valor]) => {
    // Normalizar: convertir de [-max, max] a [0, 3]
    const normalizado = ((valor + maxAbsoluto) / (2 * maxAbsoluto)) * 3;
    perfilUsuario[eje] = Math.round(normalizado * 10) / 10; // Redondear a 1 decimal
  });

  return perfilUsuario;
}

// =============================================================================
// CONSTRUIR FLAGS DESDE TOLERANCIAS
// =============================================================================
export function buildFlags(tolerancias) {
  const flags = {};

  // no_guardias = (guardias <= 1)
  if (tolerancias.guardias <= 1) {
    flags.no_guardias = true;
  }

  // aversion_quirofano = (quirofano_sangre <= 1)
  if (tolerancias.quirofano_sangre <= 1) {
    flags.aversion_quirofano = true;
  }

  // no_ninos = (ninos <= 1)
  if (tolerancias.ninos <= 1) {
    flags.no_ninos = true;
  }

  // aversion_emocional = (emocional_intenso <= 1)
  if (tolerancias.emocional_intenso <= 1) {
    flags.aversion_emocional = true;
  }

  // aversion_radiacion = (radiacion <= 1)
  if (tolerancias.radiacion <= 1) {
    flags.aversion_radiacion = true;
  }

  return flags;
}

// =============================================================================
// CALCULAR SCORE BASE POR ESPECIALIDAD
// ScoreBase(especialidad) = SUMA( U_eje * ADN_eje )
// =============================================================================
export function calcularScoreBase(perfilUsuario, especialidadId) {
  const especialidad = ADN_ESPECIALIDADES[especialidadId];
  if (!especialidad) return 0;

  let score = 0;
  Object.keys(perfilUsuario).forEach(eje => {
    const uEje = perfilUsuario[eje] || 0;
    const adnEje = especialidad.ejes[eje] || 0;
    score += uEje * adnEje;
  });

  return score;
}

// =============================================================================
// APLICAR PENALIZADORES TRADICIONALES
// =============================================================================
export function aplicarPenalizadores(scoreBase, flags, especialidadId) {
  const especialidad = ADN_ESPECIALIDADES[especialidadId];
  if (!especialidad) return { scoreFinal: scoreBase, penalizadoresAplicados: [] };

  let scoreFinal = scoreBase;
  const penalizadoresAplicados = [];

  Object.entries(PENALIZADORES).forEach(([flagKey, config]) => {
    if (flags[flagKey] && especialidad.ejes[config.campo_adn] >= config.umbral) {
      scoreFinal += config.penalizacion;
      penalizadoresAplicados.push({
        flag: flagKey,
        factor: MAPEO_FACTORES[flagKey],
        penalizacion: config.penalizacion
      });
    }
  });

  return { scoreFinal, penalizadoresAplicados };
}

// =============================================================================
// CALCULAR OR_COMPONENT (Componente Quirófano)
// OR_Component = OR_PREF * OR_WEIGHT * ADN.quirofano
// OR_PREF=0.5: "Me gusta el quirófano pero sin operar" (ej: Anestesiología)
// =============================================================================
export function calcularORComponent(orPref, especialidadId) {
  const especialidad = ADN_ESPECIALIDADES[especialidadId];
  if (!especialidad || orPref === null || orPref === undefined) return 0;

  const adnQuirofano = especialidad.ejes.quirofano || 0;
  const adnProcedimientos = especialidad.ejes.procedimientos || 0;
  
  // Caso especial: OR_PREF = 0.5 ("quirófano sin operar")
  if (orPref === 0.5) {
    // Base: bonificación moderada para cualquier especialidad con quirófano
    let component = 0.5 * SCORING_WEIGHTS.OR_WEIGHT * adnQuirofano / 3;
    
    // Bonus extra para especialidades que están EN quirófano pero NO son puramente quirúrgicas
    // quirofano=2 y procedimientos>=2 → Anestesiología y similares
    if (adnQuirofano === 2 && adnProcedimientos >= 2) {
      component += SCORING_WEIGHTS.OR_SIN_OPERAR_BONUS;
    }
    
    // Penalización leve para cirugías puras (quirofano=3) — no es lo que busca
    if (adnQuirofano >= 3) {
      component -= SCORING_WEIGHTS.OR_SIN_OPERAR_CIRUGIA_PENALTY;
    }
    
    return component;
  }

  return orPref * SCORING_WEIGHTS.OR_WEIGHT * adnQuirofano / 3; // Normalizado
}

// =============================================================================
// CALCULAR PROC_COMPONENT (Componente Procedimientos No Quirúrgicos)
// =============================================================================
export function calcularPROCComponent(orPref, nonOrStyle, procIntensity, especialidadId) {
  const especialidad = ADN_ESPECIALIDADES[especialidadId];
  if (!especialidad) return 0;

  const adnProc = especialidad.ejes.procedimientos || 0;
  const adnQuir = especialidad.ejes.quirofano || 0;
  const adnLong = especialidad.ejes.longitudinal || 0;

  // Solo aplica si OR_PREF = -1 (prefiere fuera de quirófano)
  if (orPref !== -1) return 0;

  if (nonOrStyle === 'procedimientos') {
    // Le gustan procedimientos fuera de quirófano
    if (adnProc >= 2 && adnQuir <= 1) {
      // Especialidad procedimental no quirúrgica - bonificar
      const weight = procIntensity === 'complejo' 
        ? SCORING_WEIGHTS.PROC_WEIGHT_COMPLEJO 
        : SCORING_WEIGHTS.PROC_WEIGHT_SIMPLE;
      return weight;
    }
    return 0;
  }

  if (nonOrStyle === 'clinica') {
    // Prefiere clínica sin procedimientos
    if (adnProc >= 2) {
      // Penalizar especialidades muy procedimentales
      return SCORING_WEIGHTS.CLINICA_SIN_PROC_PENALTY;
    }
    if (adnLong >= 2 && adnProc <= 1) {
      // Bonificar especialidades longitudinales/consulta
      return SCORING_WEIGHTS.CLINICA_LONGITUDINAL_BONUS;
    }
    return 0;
  }

  return 0;
}

// =============================================================================
// CALCULAR BONUS POR DESTINO (mejorado con categorías)
// =============================================================================
export function calcularBonusDestino(especialidadId, destinoId, friccionTroncoAlta, nonOrStyle, procIntensity) {
  if (!destinoId) return 0;

  const destino = DESTINOS_10_ANIOS.find(d => d.destino_id === destinoId);
  if (!destino) return 0;

  let bonus = 0;

  // Verificar si esta especialidad habilita el destino
  const especialidadHabilita = destino.troncos_habilitantes.includes(especialidadId);
  
  if (especialidadHabilita && !friccionTroncoAlta) {
    // Bonus base por habilitar destino
    bonus += 10;
    
    // Bonus adicional si el destino es procedimental y el usuario quiere procedimientos
    if (nonOrStyle === 'procedimientos' && CATEGORIAS_PROCEDIMENTALES.includes(destino.categoria)) {
      bonus += procIntensity === 'complejo' 
        ? SCORING_WEIGHTS.DESTINO_BONUS_COMPLEJO 
        : SCORING_WEIGHTS.DESTINO_BONUS_SIMPLE;
    }
  }

  return bonus;
}

// =============================================================================
// VERIFICAR HARD STOPS (Reglas de Exclusión para Top1)
// =============================================================================
export function verificarHardStops(especialidadId, orPref, nonOrStyle, tolerancias, flags) {
  const especialidad = ADN_ESPECIALIDADES[especialidadId];
  if (!especialidad) return { blocked: false, reason: null };

  const hardStops = [];

  // STRONG_NO_OR = (OR_PREF=-1 AND tolerancia_quirofano_sangre <=1)
  // Nota: OR_PREF=0.5 ("quirófano sin operar") NO activa este hard stop
  const strongNoOr = orPref === -1 && tolerancias.quirofano_sangre <= 1;
  
  // STRONG_NO_PROC = (OR_PREF=-1 AND NON_OR_STYLE=clinica)
  const strongNoProc = orPref === -1 && nonOrStyle === 'clinica';

  // Regla A: Si STRONG_NO_OR = true, ninguna con ADN.quirofano >= 2 puede ser Top1
  if (strongNoOr && especialidad.ejes.quirofano >= 2) {
    hardStops.push({
      rule: 'STRONG_NO_OR',
      factor: 'quirófano',
      message: PLANTILLAS.HARD_STOP_ACTIVADO(especialidad.nombre, 'quirófano')
    });
  }

  // Regla B: Si STRONG_NO_PROC = true, ninguna con ADN.procedimientos >= 2 puede ser Top1
  if (strongNoProc && especialidad.ejes.procedimientos >= 2) {
    hardStops.push({
      rule: 'STRONG_NO_PROC',
      factor: 'procedimientos invasivos',
      message: PLANTILLAS.HARD_STOP_ACTIVADO(especialidad.nombre, 'procedimientos invasivos')
    });
  }

  // Regla C: Si no_guardias=true y ADN.agudo >= 3, no puede ser Top1
  if (flags.no_guardias && especialidad.ejes.agudo >= 3) {
    hardStops.push({
      rule: 'NO_GUARDIAS_AGUDO',
      factor: 'guardias y ambiente agudo',
      message: PLANTILLAS.HARD_STOP_ACTIVADO(especialidad.nombre, 'guardias/ambiente agudo')
    });
  }

  return {
    blocked: hardStops.length > 0,
    hardStops
  };
}

// =============================================================================
// CALCULAR SCORES COMPLETO DE TODAS LAS ESPECIALIDADES (NUEVO ALGORITMO)
// ScoreFinal = ScoreBase + Penalties + OR_Component + PROC_Component + DestinoBonus
// =============================================================================
export function scoreSpecialties(
  perfilUsuario, 
  flags, 
  destinoId = null, 
  friccionTroncoAlta = false, 
  subsObjetivo = [],
  orPref = null,        // NUEVO: +1 o -1
  nonOrStyle = null,    // NUEVO: 'procedimientos' | 'clinica' | null
  procIntensity = null, // NUEVO: 'simple' | 'complejo' | null
  tolerancias = {}      // NUEVO: para hard stops
) {
  const resultados = [];

  Object.keys(ADN_ESPECIALIDADES).forEach(especialidadId => {
    const especialidad = ADN_ESPECIALIDADES[especialidadId];
    
    // 1. Score base (producto punto perfil × ADN)
    const scoreBase = calcularScoreBase(perfilUsuario, especialidadId);
    
    // 2. Aplicar penalizadores tradicionales
    const { scoreFinal: scoreConPenalizadores, penalizadoresAplicados } = 
      aplicarPenalizadores(scoreBase, flags, especialidadId);
    
    // 3. OR_Component (quirófano)
    const orComponent = calcularORComponent(orPref, especialidadId);
    
    // 4. PROC_Component (procedimientos no quirúrgicos)
    const procComponent = calcularPROCComponent(orPref, nonOrStyle, procIntensity, especialidadId);
    
    // 5. Bonus por destino
    const destinoBonus = calcularBonusDestino(
      especialidadId, 
      destinoId, 
      friccionTroncoAlta,
      nonOrStyle,
      procIntensity
    );

    // 6. Bonus suave por subs objetivo (+5 por cada sub que coincida)
    let subsBonus = 0;
    if (subsObjetivo.length > 0) {
      const subsDeEstaEspecialidad = DESTINOS_10_ANIOS
        .filter(d => d.troncos_habilitantes.includes(especialidadId))
        .map(d => d.destino_nombre.toLowerCase());
      
      subsObjetivo.forEach(sub => {
        if (subsDeEstaEspecialidad.some(s => s.includes(sub.toLowerCase()))) {
          subsBonus += 5;
        }
      });
    }

    // Score final compuesto
    const scoreFinal = scoreConPenalizadores + orComponent + procComponent + destinoBonus + subsBonus;

    // 7. Verificar hard stops
    const hardStopInfo = verificarHardStops(especialidadId, orPref, nonOrStyle, tolerancias, flags);

    // Calcular contribuciones por eje (para explicación)
    const contribuciones = [];
    Object.keys(perfilUsuario).forEach(eje => {
      const uEje = perfilUsuario[eje] || 0;
      const adnEje = especialidad.ejes[eje] || 0;
      contribuciones.push({
        eje,
        nombreEje: NOMBRES_EJES[eje],
        contribucion: uEje * adnEje
      });
    });
    contribuciones.sort((a, b) => b.contribucion - a.contribucion);

    resultados.push({
      especialidadId,
      nombre: especialidad.nombre,
      abreviatura: especialidad.abreviatura,
      scoreBase,
      scoreFinal,
      scoreComponents: {
        base: scoreBase,
        penalizadores: scoreConPenalizadores - scoreBase,
        orComponent,
        procComponent,
        destinoBonus,
        subsBonus
      },
      penalizadoresAplicados,
      hardStopBlocked: hardStopInfo.blocked,
      hardStops: hardStopInfo.hardStops,
      top3Contribuciones: contribuciones.slice(0, 3),
      adnEjes: especialidad.ejes
    });
  });

  // Ordenar por score final descendente
  resultados.sort((a, b) => b.scoreFinal - a.scoreFinal);

  return resultados;
}

// =============================================================================
// CALCULAR SIMILITUD COSENO ENTRE DOS ESPECIALIDADES
// =============================================================================
function calcularSimilitudCoseno(adnA, adnB) {
  let dotProduct = 0;
  let normA = 0;
  let normB = 0;

  Object.keys(adnA).forEach(eje => {
    const a = adnA[eje] || 0;
    const b = adnB[eje] || 0;
    dotProduct += a * b;
    normA += a * a;
    normB += b * b;
  });

  if (normA === 0 || normB === 0) return 0;
  return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB));
}

// =============================================================================
// CALCULAR DISTANCIA L1 ENTRE DOS ESPECIALIDADES
// =============================================================================
function calcularDistanciaL1(adnA, adnB) {
  let distancia = 0;
  Object.keys(adnA).forEach(eje => {
    distancia += Math.abs((adnA[eje] || 0) - (adnB[eje] || 0));
  });
  return distancia;
}

// =============================================================================
// SELECCIONAR TOP 2 CON DIVERSIDAD Y HARD STOPS
// Top1 = mayor ScoreFinal que NO esté bloqueado
// Top2 = siguiente diverso que NO esté bloqueado
// =============================================================================
export function pickTop2Diverse(resultadosOrdenados) {
  if (resultadosOrdenados.length === 0) return { top2: [], hardStopsActivados: [] };
  
  const hardStopsActivados = [];
  
  // Filtrar candidatos para Top1 (no bloqueados por hard stops)
  const candidatosTop1 = resultadosOrdenados.filter(r => !r.hardStopBlocked);
  
  // Si todos están bloqueados, usar el primero pero marcar confianza baja
  let top1;
  let relaxedHardStop = false;
  
  if (candidatosTop1.length === 0) {
    // Relajar reglas - usar el de mayor score pero registrar
    top1 = resultadosOrdenados[0];
    relaxedHardStop = true;
    if (top1.hardStops?.length > 0) {
      hardStopsActivados.push(...top1.hardStops);
    }
  } else {
    top1 = candidatosTop1[0];
    
    // Registrar si el top original fue bloqueado
    if (resultadosOrdenados[0].hardStopBlocked && resultadosOrdenados[0].especialidadId !== top1.especialidadId) {
      hardStopsActivados.push(...resultadosOrdenados[0].hardStops);
    }
  }

  if (resultadosOrdenados.length === 1) {
    return { 
      top2: [top1], 
      hardStopsActivados,
      relaxedHardStop 
    };
  }

  // Buscar top2 que sea diverso y no bloqueado
  const candidatosTop2 = resultadosOrdenados.filter(r => 
    r.especialidadId !== top1.especialidadId && !r.hardStopBlocked
  );

  for (const candidato of candidatosTop2) {
    const similitud = calcularSimilitudCoseno(top1.adnEjes, candidato.adnEjes);
    const distanciaL1 = calcularDistanciaL1(top1.adnEjes, candidato.adnEjes);
    
    // Es diverso si similitud < 0.90 O distancia L1 >= 4
    if (similitud < 0.90 || distanciaL1 >= 4) {
      return { 
        top2: [top1, candidato], 
        hardStopsActivados,
        relaxedHardStop 
      };
    }
  }

  // Si no hay diverso no bloqueado, tomar el siguiente disponible
  const segundoDisponible = candidatosTop2[0] || resultadosOrdenados.find(r => 
    r.especialidadId !== top1.especialidadId
  );

  if (segundoDisponible) {
    return { 
      top2: [top1, segundoDisponible], 
      hardStopsActivados,
      relaxedHardStop 
    };
  }

  return { 
    top2: [top1], 
    hardStopsActivados,
    relaxedHardStop 
  };
}

// =============================================================================
// CALCULAR INCONSISTENCIAS (MEJORADO con exposición)
// =============================================================================
export function calcularInconsistencias(perfilUsuario, tolerancias, orPref = null, exposureLevel = null) {
  const inconsistencias = [];

  // Inconsistencia_guardias = (U.agudo >= 2) AND (guardias <= 1)
  if (perfilUsuario.agudo >= 2 && tolerancias.guardias <= 1) {
    inconsistencias.push({
      tipo: 'guardias',
      descripcion: 'Tu perfil indica gusto por ambientes agudos, pero marcaste baja tolerancia a guardias.'
    });
  }

  // Inconsistencia_quirofano = (U.quirofano >= 2) AND (quirofano_sangre <= 1)
  if (perfilUsuario.quirofano >= 2 && tolerancias.quirofano_sangre <= 1) {
    inconsistencias.push({
      tipo: 'quirofano',
      descripcion: 'Tu perfil indica gusto por quirófano, pero marcaste baja tolerancia a quirófano y sangre.'
    });
  }

  // Inconsistencia_emocional = (U.emocional >= 2) AND (emocional_intenso <= 1)
  if (perfilUsuario.emocional >= 2 && tolerancias.emocional_intenso <= 1) {
    inconsistencias.push({
      tipo: 'emocional',
      descripcion: 'Tu perfil indica gusto por carga emocional, pero marcaste baja tolerancia a trato emocional intenso.'
    });
  }

  // Inconsistencia_pediatria = (U.pediatria >= 2) AND (ninos <= 1)
  if (perfilUsuario.pediatria >= 2 && tolerancias.ninos <= 1) {
    inconsistencias.push({
      tipo: 'pediatria',
      descripcion: 'Tu perfil indica gusto por pediatría, pero marcaste baja tolerancia a niños.'
    });
  }

  // NUEVO: Inconsistencia por exposición baja con decisiones extremas
  if (exposureLevel === 'baja') {
    // OR_PREF=+1 con tolerancia_quirofano<=1 es sospechoso
    if (orPref === 1 && tolerancias.quirofano_sangre <= 1) {
      inconsistencias.push({
        tipo: 'exposure_or',
        descripcion: 'Indicaste que te gusta el quirófano pero baja tolerancia y poca experiencia real. Considera validar con shadowing.'
      });
    }
    
    // OR_PREF=0.5 con tolerancia_quirofano<=1 es moderadamente sospechoso
    if (orPref === 0.5 && tolerancias.quirofano_sangre <= 1) {
      inconsistencias.push({
        tipo: 'exposure_or_sin_operar',
        descripcion: 'Indicaste que te gusta el quirófano sin operar pero baja tolerancia a quirófano/sangre. Considera validar con una rotación por anestesiología.'
      });
    }
    
    // no_guardias=true con perfil agudo alto es sospechoso
    if (tolerancias.guardias <= 1 && perfilUsuario.agudo >= 2) {
      inconsistencias.push({
        tipo: 'exposure_guardias',
        descripcion: 'Tienes perfil agudo pero rechazas guardias con poca experiencia real. Considera validar con rotación.'
      });
    }
  }

  return inconsistencias;
}

// =============================================================================
// CALCULAR CONFIANZA (MEJORADO con hard stops y exposure)
// =============================================================================
export function calcularConfianza(
  top2Result, 
  inconsistencias, 
  friccionTroncoAlta, 
  hardStopsActivados = [],
  relaxedHardStop = false,
  exposureLevel = null
) {
  const { top2 } = top2Result;
  const totalInconsistencias = inconsistencias.length;
  
  // Calcular delta entre top1 y top2
  const delta = top2.length >= 2 
    ? top2[0].scoreFinal - top2[1].scoreFinal 
    : 100; // Si solo hay 1, delta es alto

  // Determinar nivel de delta
  const deltaAlto = delta >= 15;
  const deltaMedio = delta >= 5 && delta < 15;
  const deltaBajo = delta < 5;

  // Factores que bajan confianza
  let factoresBaja = 0;
  const motivos = [];

  // Hard stops activados bajan confianza obligatoriamente
  if (hardStopsActivados.length > 0 || relaxedHardStop) {
    factoresBaja += 2;
    motivos.push('Se activaron filtros de exclusión por choques fuertes con tu perfil.');
  }

  // Exposición baja
  if (exposureLevel === 'baja') {
    factoresBaja += 1;
    motivos.push(PLANTILLAS.EXPOSURE_BAJA);
  }

  // Inconsistencias
  if (totalInconsistencias >= 4) {
    factoresBaja += 2;
    motivos.push(`Detecté ${totalInconsistencias} inconsistencias en tus respuestas.`);
  } else if (totalInconsistencias >= 2) {
    factoresBaja += 1;
    motivos.push(`Detecté ${totalInconsistencias} inconsistencias en tus respuestas.`);
  }

  // Delta bajo
  if (deltaBajo) {
    factoresBaja += 1;
    motivos.push('La diferencia entre tus dos mejores opciones es muy pequeña.');
  }

  // Fricción tronco
  if (friccionTroncoAlta) {
    factoresBaja += 1;
    motivos.push('Hay fricción entre tu destino deseado y el tronco necesario.');
  }

  // Calcular nivel
  let nivel;
  if (factoresBaja >= 3 || relaxedHardStop) {
    nivel = 'baja';
  } else if (factoresBaja === 0 && deltaAlto && !friccionTroncoAlta) {
    nivel = 'alta';
    motivos.push('Tus respuestas son consistentes.');
    motivos.push('Hay una opción claramente destacada.');
  } else {
    nivel = 'media';
    if (deltaMedio && motivos.length === 0) {
      motivos.push('Dos opciones compiten de cerca.');
    }
  }

  // Acción sugerida según nivel
  let accionSugerida;
  switch (nivel) {
    case 'alta':
      accionSugerida = PLANTILLAS.CONFIANZA_ALTA;
      break;
    case 'media':
      accionSugerida = PLANTILLAS.CONFIANZA_MEDIA;
      break;
    case 'baja':
      accionSugerida = PLANTILLAS.CONFIANZA_BAJA;
      break;
    default:
      accionSugerida = PLANTILLAS.CONFIANZA_MEDIA;
  }

  // Cantidad de razones/advertencias según nivel
  let numRazones, numAdvertencias;
  switch (nivel) {
    case 'alta':
      numRazones = 3;
      numAdvertencias = 0;
      break;
    case 'media':
      numRazones = 2;
      numAdvertencias = 1;
      break;
    case 'baja':
      numRazones = 1;
      numAdvertencias = 2;
      break;
    default:
      numRazones = 2;
      numAdvertencias = 1;
  }

  return {
    nivel,
    motivos,
    accionSugerida,
    delta,
    totalInconsistencias,
    hardStopsActivados,
    relaxedHardStop,
    exposureBaja: exposureLevel === 'baja',
    displayConfig: { numRazones, numAdvertencias }
  };
}

// =============================================================================
// GENERAR PUNTO CLAVE SEGÚN PERFIL OR/PROCEDIMIENTOS
// =============================================================================
export function generarPuntoClave(orPref, nonOrStyle) {
  if (orPref === 1) {
    return PLANTILLAS.PUNTO_CLAVE_OR_SI;
  }
  if (orPref === 0.5) {
    return PLANTILLAS.PUNTO_CLAVE_OR_SIN_OPERAR;
  }
  if (orPref === -1) {
    if (nonOrStyle === 'procedimientos') {
      return PLANTILLAS.PUNTO_CLAVE_OR_NO_PROC;
    }
    if (nonOrStyle === 'clinica') {
      return PLANTILLAS.PUNTO_CLAVE_OR_NO_CLINICA;
    }
  }
  return null;
}

// =============================================================================
// GENERAR EXPLICACIONES DESDE PLANTILLAS (MEJORADO)
// =============================================================================
export function buildExplanationsFromTemplates(especialidad, perfilUsuario, penalizadores, confianza) {
  const top3 = especialidad.top3Contribuciones;
  const { numRazones, numAdvertencias } = confianza.displayConfig;
  
  // Razones (limitadas según confianza)
  const razonesCompletas = [
    top3[0]?.nombreEje,
    top3[1]?.nombreEje,
    top3[2]?.nombreEje
  ].filter(Boolean);
  
  const razonesTexto = razonesCompletas.slice(0, numRazones).join(', ');
  const razones = numRazones > 0 
    ? `Te conviene por tu afinidad con: ${razonesTexto}.`
    : '';

  // Banderas rojas (limitadas según confianza)
  const banderasRojas = penalizadores.slice(0, numAdvertencias + 1).map(p => 
    PLANTILLAS.BANDERA_ROJA(p.factor)
  );

  // Advertencias adicionales por hard stops
  const advertenciasHardStop = confianza.hardStopsActivados?.map(hs => hs.message) || [];

  return {
    razones,
    banderasRojas,
    advertenciasHardStop
  };
}

// =============================================================================
// OBTENER INFORMACIÓN DE DESTINO
// =============================================================================
export function getDestinoInfo(destinoId) {
  return DESTINOS_10_ANIOS.find(d => d.destino_id === destinoId);
}

// =============================================================================
// VERIFICAR FRICCIÓN TRONCO-DESTINO
// =============================================================================
export function verificarFriccionTronco(destinoId, flags) {
  const destino = getDestinoInfo(destinoId);
  if (!destino) return { hayFriccion: false, conflictos: [], mensaje: '' };

  // Encontrar conflictos entre flags del usuario y flags_conflicto del destino
  const conflictos = [];
  destino.flags_conflicto_tipicos.forEach(flagConflicto => {
    if (flags[flagConflicto]) {
      conflictos.push({
        flag: flagConflicto,
        factor: MAPEO_FACTORES[flagConflicto]
      });
    }
  });

  // Generar mensaje
  let mensaje = '';
  if (conflictos.length > 0) {
    if (destinoId === 'D01') {
      mensaje = PLANTILLAS.FRICCION_CARDIOLOGIA_INTERVENCIONISTA;
    } else {
      const troncoNombre = ADN_ESPECIALIDADES[destino.troncos_habilitantes[0]]?.nombre || destino.troncos_habilitantes[0];
      const requisitos = destino.tronco_requisitos_duros.join(', ');
      const flagsTexto = conflictos.map(c => c.factor).join(', ');
      mensaje = PLANTILLAS.FRICCION_TRONCO(destino.destino_nombre, troncoNombre, requisitos, flagsTexto);
    }
  }

  return {
    hayFriccion: conflictos.length > 0,
    conflictos,
    mensaje,
    alternativas: destino.alternativas_si_no_toleras_tronco
  };
}

// =============================================================================
// FUZZY MATCH PARA DESTINOS (búsqueda por nombre)
// =============================================================================
export function fuzzyMatchDestino(texto) {
  if (!texto || texto.length < 2) return [];

  const textoLower = texto.toLowerCase().trim();
  
  const matches = DESTINOS_10_ANIOS
    .filter(d => {
      const nombreLower = d.destino_nombre.toLowerCase();
      return nombreLower.includes(textoLower) || 
             textoLower.includes(nombreLower.split(' ')[0]);
    })
    .slice(0, 10); // Máximo 10 sugerencias

  return matches;
}
